"use no memo";

// @tanstack/react-table v8 keeps its state in a mutable `table` instance whose identity is stable
// across updates, so React Compiler memoization would skip re-renders that the table needs. Remove
// this opt-out when the table upgrades to v9, which is compiler-safe.
import type { ColumnSizingState, Table } from "@tanstack/react-table";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { MIN_COLUMN_WIDTH } from "./constants";

interface UseColumnSizingOptions<T> {
  tableWidth: number | null;
  columnVisibility: Record<string, boolean>;
  visibleColumnIds: string[];
  getTable: () => Table<T> | null;
  horizontalOverflow?: boolean;
}

interface UseColumnSizingReturn {
  columnSizing: ColumnSizingState;
  handleColumnSizingChange: (
    updaterOrValue: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState),
  ) => void;
  /**
   * Grow a single column by up to `extraWidth` pixels to fit its content.
   * In fixed-width mode the space is pulled from other columns (down to MIN_COLUMN_WIDTH),
   * so the column may not fully fit its content when space runs out. In horizontal-overflow
   * mode the table simply grows wider.
   */
  maximizeColumn: (colId: string, extraWidth: number) => void;
  /**
   * Grow multiple columns by their respective `extraWidth` pixels to fit their content.
   * Intended for horizontal-overflow mode, where every column can grow at once and the
   * table scrolls; other columns are left untouched.
   */
  maximizeColumns: (extraWidthByColId: Record<string, number>) => void;
}

/**
 * Splits `width` across `count` columns in whole pixels, giving the division remainder to the leading columns so the parts add up to exactly `width`.
 */
function splitIntoWholePixels(width: number, count: number): number[] {
  const total = Math.floor(width);
  const base = Math.trunc(total / count);
  let remainder = total - base * count;

  return Array.from({ length: count }, () => {
    const step = Math.sign(remainder);
    remainder -= step;
    return base + step;
  });
}

/**
 * Adds `delta` px across `ids` in whole pixels, never taking a column below MIN_COLUMN_WIDTH.
 * Returns the px that could not be absorbed, which is non-zero only when tableWidth < MIN_COLUMN_WIDTH × column count.
 */
function distributeWidth(sizing: ColumnSizingState, ids: string[], delta: number): number {
  let remaining = Math.trunc(delta);
  let adjustable = ids;

  while (remaining !== 0 && adjustable.length > 0) {
    const shares = splitIntoWholePixels(remaining, adjustable.length);
    const stillAdjustable: string[] = [];
    adjustable.forEach((id, i) => {
      const current = sizing[id] ?? 0;
      const target = current + shares[i];
      const clamped = Math.max(MIN_COLUMN_WIDTH, target);
      sizing[id] = clamped;
      remaining -= clamped - current;
      if (target >= MIN_COLUMN_WIDTH) {
        stillAdjustable.push(id);
      }
    });
    if (stillAdjustable.length === adjustable.length) {
      break;
    }
    adjustable = stillAdjustable;
  }

  return remaining;
}

/**
 * Manages column sizing state for DataTable.
 * Handles:
 * - Initial distribution of widths based on preset widths and tableWidth
 * - Adjacent column transfer during resize operations
 * - Minimum width enforcement
 */
export function useColumnSizing<T>({
  tableWidth,
  columnVisibility,
  visibleColumnIds,
  getTable,
  horizontalOverflow,
}: UseColumnSizingOptions<T>): UseColumnSizingReturn {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const prevColumnSizingRef = useRef<ColumnSizingState>({});

  const handleColumnSizingChange = useCallback(
    (updaterOrValue: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)) => {
      const prevSizing = prevColumnSizingRef.current;

      // Don't process sizing changes until useLayoutEffect has established initial sizing
      if (Object.keys(prevSizing).length === 0) {
        return;
      }

      const newSizing = typeof updaterOrValue === "function" ? updaterOrValue(columnSizing) : { ...updaterOrValue };
      const table = getTable();

      const getColSize = (colId: string) => prevSizing[colId] ?? table?.getColumn(colId)?.getSize() ?? 0;

      // Find which column was resized and calculate the delta
      let resizedColId: string | undefined;
      let delta = 0;
      for (const colId of Object.keys(newSizing)) {
        const newSize = newSizing[colId];
        const prevSize = getColSize(colId);
        if (newSize !== prevSize && newSize !== undefined) {
          resizedColId = colId;
          delta = newSize - prevSize;
          break;
        }
      }

      if (resizedColId && delta !== 0 && table) {
        if (horizontalOverflow) {
          // Horizontal overflow mode: resize only the dragged column, changing total table width
          const currentSize = getColSize(resizedColId);
          if (delta > 0) {
            newSizing[resizedColId] = currentSize + delta;
          } else {
            const minAllowedDelta = MIN_COLUMN_WIDTH - currentSize;
            const cappedDelta = Math.max(delta, minAllowedDelta);
            if (cappedDelta >= 0) {
              return;
            }
            newSizing[resizedColId] = currentSize + cappedDelta;
          }
        } else {
          // Fixed-width mode: transfer delta to adjacent column to preserve total width
          const colIndex = visibleColumnIds.indexOf(resizedColId);
          const nextColId = visibleColumnIds[colIndex + 1];

          if (delta > 0) {
            if (!nextColId) {
              return;
            }
            const nextColCurrentSize = getColSize(nextColId);
            const availableSpace = nextColCurrentSize - MIN_COLUMN_WIDTH;
            if (availableSpace <= 0) {
              return;
            }
            const cappedDelta = Math.min(delta, availableSpace);
            newSizing[resizedColId] = getColSize(resizedColId) + cappedDelta;
            newSizing[nextColId] = nextColCurrentSize - cappedDelta;
          } else {
            const currentSize = getColSize(resizedColId);
            const minAllowedDelta = MIN_COLUMN_WIDTH - currentSize;
            const cappedDelta = Math.max(delta, minAllowedDelta);
            if (cappedDelta >= 0) {
              return;
            }
            newSizing[resizedColId] = currentSize + cappedDelta;
            if (nextColId) {
              newSizing[nextColId] = getColSize(nextColId) - cappedDelta;
            }
          }
        }
      }

      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    },
    [columnSizing, visibleColumnIds, getTable, horizontalOverflow],
  );

  // Reads the current width of every visible column, preferring the last committed sizing and
  // falling back to react-table's computed size before the initial layout effect has run.
  const getCurrentSizing = useCallback((): ColumnSizingState => {
    const table = getTable();
    const sizing: ColumnSizingState = {};
    for (const col of table?.getVisibleLeafColumns() ?? []) {
      sizing[col.id] = prevColumnSizingRef.current[col.id] ?? col.getSize();
    }
    return sizing;
  }, [getTable]);

  const maximizeColumn = useCallback(
    (colId: string, extraWidth: number) => {
      if (extraWidth <= 0) {
        return;
      }
      const current = getCurrentSizing();
      if (current[colId] === undefined) {
        return;
      }

      const newSizing: ColumnSizingState = { ...current };

      if (horizontalOverflow) {
        // Table can grow, so just widen the target column.
        newSizing[colId] = current[colId] + extraWidth;
      } else {
        // Pull the needed space from other columns, proportional to how much each can spare
        // (never below MIN_COLUMN_WIDTH). Keeps the total width equal to the container.
        // Exclude columns that opt out of resizing so maximizing one can't shrink a locked one.
        const table = getTable();
        const others = visibleColumnIds.filter(
          (id) => id !== colId && current[id] !== undefined && table?.getColumn(id)?.getCanResize() !== false,
        );
        const available: Record<string, number> = {};
        let totalAvailable = 0;
        for (const id of others) {
          const avail = Math.max(0, current[id] - MIN_COLUMN_WIDTH);
          available[id] = avail;
          totalAvailable += avail;
        }
        if (totalAvailable <= 0) {
          return;
        }
        const grant = Math.min(extraWidth, totalAvailable);

        let distributed = 0;
        for (const id of others) {
          if (distributed >= grant) {
            break;
          }
          const proportional = Math.round((available[id] / totalAvailable) * grant);
          const share = Math.min(proportional, available[id], grant - distributed);
          newSizing[id] = current[id] - share;
          distributed += share;
        }
        // Assign any rounding remainder to columns that still have room to spare.
        if (distributed < grant) {
          for (const id of others) {
            if (distributed >= grant) {
              break;
            }
            const remaining = Math.min(newSizing[id] - MIN_COLUMN_WIDTH, grant - distributed);
            if (remaining > 0) {
              newSizing[id] -= remaining;
              distributed += remaining;
            }
          }
        }
        newSizing[colId] = current[colId] + distributed;
      }

      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    },
    [getCurrentSizing, getTable, horizontalOverflow, visibleColumnIds],
  );

  const maximizeColumns = useCallback(
    (extraWidthByColId: Record<string, number>) => {
      const current = getCurrentSizing();
      const newSizing: ColumnSizingState = { ...current };
      let changed = false;
      for (const id of visibleColumnIds) {
        const extra = extraWidthByColId[id] ?? 0;
        if (extra > 0 && current[id] !== undefined) {
          newSizing[id] = current[id] + extra;
          changed = true;
        }
      }
      if (!changed) {
        return;
      }
      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    },
    [getCurrentSizing, visibleColumnIds],
  );

  // Store getTable in a ref to avoid re-running effect when function reference changes
  const getTableRef = useRef(getTable);
  getTableRef.current = getTable;

  // Serialize columnVisibility for stable dependency comparison
  const columnVisibilityKey = JSON.stringify(columnVisibility);

  useLayoutEffect(() => {
    const table = getTableRef.current();
    const visibleCols = table?.getVisibleLeafColumns();
    if (!tableWidth || !visibleCols?.length) {
      return;
    }

    const newSizing: ColumnSizingState = {};

    // Collect preset widths (from width prop) and columns needing distribution
    let presetTotal = 0;
    const colsNeedingDistribution: typeof visibleCols = [];

    for (const col of visibleCols) {
      const presetSize = col.columnDef.size === undefined ? undefined : Math.floor(col.columnDef.size);
      if (presetSize !== undefined) {
        newSizing[col.id] = presetSize;
        presetTotal += presetSize;
      } else {
        colsNeedingDistribution.push(col);
      }
    }

    // Distribute remaining width equally to columns without presets
    if (colsNeedingDistribution.length > 0) {
      const shares = splitIntoWholePixels(tableWidth - presetTotal, colsNeedingDistribution.length);
      colsNeedingDistribution.forEach((col, i) => {
        newSizing[col.id] = Math.max(MIN_COLUMN_WIDTH, shares[i]);
      });
    }

    // If total doesn't match tableWidth, distribute the difference across ALL columns. This is also
    // what absorbs the overage when the equal split above had to clamp a column up to the floor.
    // In horizontal overflow mode, only equalize when columns under-fill the table width
    // (don't shrink columns that the user has expanded beyond the container).
    // A leftover here means tableWidth is under MIN_COLUMN_WIDTH * column count, so the table has to overflow.
    const total = Object.values(newSizing).reduce((sum, size) => sum + size, 0);
    if (total !== tableWidth && (!horizontalOverflow || total < tableWidth)) {
      distributeWidth(
        newSizing,
        visibleCols.map((col) => col.id),
        tableWidth - total,
      );
    }

    // Only update if sizing actually changed
    const sizingChanged = visibleCols.some((col) => newSizing[col.id] !== columnSizing[col.id]);

    if (sizingChanged) {
      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    }
  }, [tableWidth, columnVisibilityKey]);

  return {
    columnSizing,
    handleColumnSizingChange,
    maximizeColumn,
    maximizeColumns,
  };
}
